import { useState } from 'react'
import { Link } from 'react-router'
import { History } from 'lucide-react'
import { events as api } from '@/api/endpoints'
import type { ActivityEntry, ActivityKind } from '@/api/types'
import { Ago } from '@/components/Ago'
import { Err } from '@/components/Err'
import { Pager } from '@/components/Pager'
import { Empty, PageHead, Section, SectionCrumb } from '@/components/layout/Page'
import { actionWord, isFailure, kindWord } from '@/domain/activity'
import { useExpert } from '@/hooks/useExpert'
import { useFallback, useRefetchOn } from '@/hooks/useEventStream'
import { usePoll } from '@/hooks/usePoll'
import { exact } from '@/ui/format'
import { pill } from './pills'

// 작업 내역(F-30) — 최근에 일어난 일의 시간순 목록. 자원마다의 이력은 그 자원 화면이 갖고,
// 여기는 "무엇이 있었나" 하나를 답한다. 줄은 시각 · 종류 · 이름 · 한 낱말 · 누가이고 그게 전부다:
// 내부 이벤트와 진단 근거는 이 화면의 것이 아니라, 백엔드가 고른 순간만 제품의 낱말로 온다.
//
// 쪽은 번호다 — 목록이 얼마나 긴지와 지금 어디쯤인지가 결정에 쓰인다. 한 쪽은 그 순간의
// 목록이고(머리에서 자라는 동안 깊은 쪽은 밀린다), 첫 쪽은 계속 다시 읽는 살아 있는 머리다.

const SIZES = [20, 50, 100] as const
type Size = (typeof SIZES)[number]
const POLL_MS = 15000

const FILTERS: { key: ActivityKind | null; label: string }[] = [
  { key: null, label: '전체' },
  { key: 'vm', label: 'VM' },
  { key: 'node', label: '노드' },
  { key: 'cluster', label: '클러스터' },
]

export default function Activity() {
  const [kind, setKind] = useState<ActivityKind | null>(null)
  return (
    <>
      <SectionCrumb />
      <PageHead
        icon={<History className="size-5" />}
        title="작업 내역"
        right={
          <div className="mt-0.5 flex flex-wrap items-center justify-end gap-2">
            {FILTERS.map((f) => (
              <button key={f.label} onClick={() => setKind(f.key)} aria-pressed={kind === f.key} className={pill(kind === f.key)}>
                {f.label}
              </button>
            ))}
          </div>
        }
      />
      {/* 걸러 내기를 바꾸면 쪽과 건수도 처음으로 돌아간다 — 같은 화면의 다른 목록이다. */}
      <Feed key={kind ?? 'all'} kind={kind} />
    </>
  )
}

function Feed({ kind }: { kind: ActivityKind | null }) {
  const [size, setSize] = useState<Size>(50)
  const [page, setPage] = useState(1)
  const head = usePoll(
    () => api.list({ ...(kind ? { kind } : {}), limit: size, offset: (page - 1) * size }),
    useFallback(POLL_MS),
    [kind, size, page],
  )
  // 어느 자원이 바뀌든 새 줄이 생겼을 수 있다 — 보고 있는 쪽을 다시 읽는다.
  useRefetchOn(() => true, head.refresh, 2000)

  const rows = head.data?.items ?? []
  const total = head.data?.total ?? 0
  const pages = Math.max(1, Math.ceil(total / size))
  // 목록이 줄어 지금 쪽이 끝을 넘으면 마지막 쪽으로 당긴다.
  if (page > pages) setPage(pages)

  const resize = (next: Size) => {
    setSize(next)
    setPage(1)
  }

  return (
    <Section
      title="최근"
      right={
        <div className="flex flex-wrap items-center justify-end gap-2">
          {SIZES.map((n) => (
            <button key={n} onClick={() => resize(n)} aria-pressed={size === n} className={pill(size === n)}>
              {n}건
            </button>
          ))}
        </div>
      }
    >
      <Err error={head.error} />
      {rows.length === 0 ? (
        <Empty>{head.loading ? '불러오는 중' : '작업 내역 없음'}</Empty>
      ) : (
        <>
          <Pager page={page} pages={pages} onPage={setPage} className="pb-3" />
          <div className="overflow-hidden rounded-lg border">
            {rows.map((row) => (
              <Row key={row.id} entry={row} />
            ))}
          </div>
          <Pager page={page} pages={pages} onPage={setPage} className="pt-3" />
        </>
      )}
    </Section>
  )
}

function Row({ entry }: { entry: ActivityEntry }) {
  const { on } = useExpert()
  const href = hrefOf(entry)
  const name = entry.subject_name ?? '이름 없음'
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 border-b bg-card px-4 py-2.5 text-[12px] last:border-b-0">
      <span className="w-20 flex-none text-muted-foreground"><Ago at={entry.at} /></span>
      <span className="w-14 flex-none text-muted-foreground">{kindWord[entry.kind]}</span>
      {/* 좁은 화면에서는 이름과 낱말이 윗줄을 다 쓰고 시각·종류·누가가 아랫줄로 내려간다.
          고정 폭을 한 줄에 늘어놓으면 줄어들 수 있는 것이 이름뿐이라 이름 칸이 0 으로 눌린다 —
          무엇이 있었나를 답하는 화면에서 이름이 먼저 사라지면 답이 남지 않는다. */}
      <span className="flex min-w-0 flex-1 items-center gap-3 max-sm:order-first max-sm:basis-full">
        <span className="min-w-0 flex-1 truncate font-medium">
          {href ? (
            <Link to={href} className="hover:underline">
              {name}
            </Link>
          ) : (
            name
          )}
        </span>
        <span className={isFailure(entry.action) ? 'flex-none text-danger-ink' : 'flex-none'}>{actionWord[entry.action]}</span>
      </span>
      <span className="w-28 flex-none truncate text-right text-muted-foreground max-sm:ml-auto">{entry.actor ?? ''}</span>
      {on ? <span className="w-40 flex-none text-right text-muted-foreground tabular-nums max-sm:basis-full">{exact(entry.at)}</span> : null}
    </div>
  )
}

// VM 줄의 주체는 임대이고 VM 상세의 키도 임대 id 다 — 줄은 늘 그 상세로 간다.
// 파기된 VM 의 줄도 남고, 그 상세는 `없는 VM` 이다.
function hrefOf(entry: ActivityEntry): string | null {
  if (entry.kind === 'node') return `/nodes/${entry.subject_id}`
  if (entry.kind === 'vm') return `/vms/${entry.subject_id}`
  return null
}
